import axios from "axios";

const API_BASE_URL = "https://api.alkajewellers.store/api";
// const API_BASE_URL = "http://localhost:8080/api";

// Create an Axios instance with base URL
const api = axios.create({
  baseURL: API_BASE_URL,
});

// Request interceptor to add JWT token to each request
api.interceptors.request.use(
  async (config) => {
    try {
      // Retrieve the token from localStorage
      const token = localStorage.getItem("token");
      if (token) {
        // Attach the token to the Authorization header
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error("Error retrieving token:", error);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Function to handle errors consistently
const handleApiError = (error) => {
  console.error("API Error:", error);
  throw error.response ? error.response.data : { message: "Network Error" };
};

// API call to fetch metal rates
export const fetchMetalRates = async () => {
  try {
    // Fetch only metals data (no margin)
    const metalsResponse = await api.get("/metals/");

    if (!metalsResponse.data) {
      throw new Error("No data received from metals API");
    }

    const apiData = metalsResponse?.data?.metals;

    // Calculate base prices without margin
    const basePrice24k = apiData.mcx_gold;
    // Karat conversions based on explicit percentages
    const basePrice22k = apiData.mcx_gold * 0.92; // 92%
    const basePrice21k = (apiData.mcx_gold * 21) / 24; // unchanged (not specified)
    const basePrice20k = apiData.mcx_gold * 0.84; // 84%
    const basePrice18k = apiData.mcx_gold * 0.76; // 76%
    const basePrice16k = (apiData.mcx_gold * 16) / 24; // unchanged (not specified)
    const basePrice14k = apiData.mcx_gold * 0.59; // 59%
    const basePrice10k = (apiData.mcx_gold * 10) / 24; // unchanged (not specified)
    const baseSilverPrice = apiData.mcx_silver_am;

    // Return data without any margin applied
    const data = {
      timestamp: Date.now(),
      metal: "XAU",
      currency: "INR",
      exchange: "MCX",
      symbol: "MCX:XAUINR",
      price: basePrice24k,
      price_gram_24k: basePrice24k,
      price_gram_22k: basePrice22k,
      price_gram_21k: basePrice21k,
      price_gram_20k: basePrice20k,
      price_gram_18k: basePrice18k,
      price_gram_16k: basePrice16k,
      price_gram_14k: basePrice14k,
      price_gram_10k: basePrice10k,
      silver_price_gram_24k: baseSilverPrice,
    };

    return data;
  } catch (error) {
    console.error("Error fetching metal rates:", error);
    return null;
  }
};

// API call to fetch carousel images
export const fetchCarouselImages = async () => {
  try {
    const response = await api.get("/banner");
    return response.data[0];
  } catch (error) {
    handleApiError(error);
  }
};

// API call to fetch categories
export const fetchCategories = async () => {
  try {
    const response = await api.get("/category");
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export const fetchMargin = async () => {
  try {
    const response = await api.get("/margin");
    return response.data;
  } catch (error) {
    console.error("Error fetching margin:", error);
    throw error;
  }
};

export const fetchProducts = async (
  endpoint,
  page = 1,
  limit = 10,
  filters = {}
) => {
  try {
    // Build query parameters
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    // Add filters to query params if they exist
    if (filters.sortBy) queryParams.append("sortBy", filters.sortBy);
    if (filters.sortOrder) queryParams.append("sortOrder", filters.sortOrder);
    if (filters.inStock) queryParams.append("inStock", "true");
    if (filters.netWeightMin && filters.netWeightMin > 0) {
      queryParams.append("netWeightMin", filters.netWeightMin.toString());
    }
    if (filters.netWeightMax && filters.netWeightMax < 500) {
      queryParams.append("netWeightMax", filters.netWeightMax.toString());
    }
    if (filters.gender) queryParams.append("gender", filters.gender);
    if (filters.isNewArrival) queryParams.append("isNewArrival", "true");
    if (filters.q) queryParams.append("q", filters.q);

    // Construct the full URL
    const separator = endpoint.includes("?") ? "&" : "?";
    const fullEndpoint = `${endpoint}${separator}${queryParams.toString()}`;

    const response = await api.get(fullEndpoint);
    return response.data;
  } catch (error) {
    console.error("Error fetching products:", error);
    throw error;
  }
};

export const fetchBestSellers = async (page = 1, limit = 10) => {
  try {
    const response = await api.get(
      `/product/best-sellers?page=${page}&limit=${limit}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching best sellers:", error);
    throw error;
  }
};

export const fetchNewArrivals = async (page = 1, limit = 10) => {
  try {
    const response = await api.get(
      `/product/new-arrivals?page=${page}&limit=${limit}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching new arrivals:", error);
    throw error;
  }
};

// Send notification to a topic
export const sendNotificationToTopic = async ({ topic, title, body }) => {
  try {
    const payload = { topic, title, body };
    const response = await api.post("/notification/send-topic", payload);
    return response?.data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Register user API call
export const registerUser = async (data) => {
  try {
    console.log("🚀 ~ registerUser ~ Start API call");
    const response = await api.post("/auth/register", data);

    // If successful, store the token and user data including role
    if (response.data.token) {
      localStorage.setItem("token", response.data.token);

      // If user data is returned, store it
      if (response.data.user) {
        localStorage.setItem("user", JSON.stringify(response.data.user));
      }
    }

    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Send OTP API Call
export const sendOtpToUserApi = async (phoneNumber) => {
  try {
    const response = await api.post("/auth/send-otp", { phoneNumber });

    if (response.data.success) {
      return true;
    } else {
      return false;
    }
  } catch (error) {
    console.error("sendOtpToUser =>", error);
    return false;
  }
};

// Verify OTP API call
export const verifyOtp = async (phoneNumber, otp, location, name) => {
  try {
    const response = await api.post("/auth/verify-otp", {
      phoneNumber,
      otp,
      name,
      location,
    });
    const data = response.data;

    if (data.success && data.user && data.token) {
      // Store user data including role
      localStorage.setItem("user", JSON.stringify(data.user));
      localStorage.setItem("token", data.token);
    }

    if (data.success) {
      return data;
    } else {
      return response;
    }
  } catch (error) {
    console.error("verifyOtp =>", error);
    return error?.response ?? error;
  }
};

export const registerNewUser = async (phoneNumber, otp, location, name) => {
  try {
    const response = await api.post("/auth/register-new-user", {
      phoneNumber,
      otp,
      name,
      location,
    });
    const data = response.data;

    if (data.success && data.user && data.token) {
      // Store user data including role
      localStorage.setItem("user", JSON.stringify(data.user));
      localStorage.setItem("token", data.token);
    }

    if (data.success) {
      return data;
    } else {
      return response;
    }
  } catch (error) {
    console.error("verifyOtp =>", error);
    return error?.response ?? error;
  }
};

export const fetchSubCategories = async (categoryId) => {
  try {
    const response = await api.get(`/category/${categoryId}/subcategories`);
    const data = response?.data;
    console.log("🚀 ~ fetchSubCategories ~ response:", response);
    return data?.subcategories;
  } catch (error) {
    console.error("Error fetching subcategories:", error);
    throw error;
  }
};

export const fetchProductsBySubcategory = async (
  subCategoryId,
  page = 1,
  limit = 10,
  filters = {}
) => {
  try {
    // Build query parameters
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(filters.sortBy && { sortBy: filters.sortBy }),
      ...(filters.sortOrder && { sortOrder: filters.sortOrder }),
      ...(filters.inStock && { inStock: "true" }),
      ...(filters.netWeightMin &&
        filters.netWeightMin > 0 && {
          netWeightMin: filters.netWeightMin.toString(),
        }),
      ...(filters.netWeightMax &&
        filters.netWeightMax < 50 && {
          netWeightMax: filters.netWeightMax.toString(),
        }),
      ...(filters.gender && { gender: filters.gender }),
      ...(filters.isNewArrival && { isNewArrival: "true" }),
    });

    const response = await api.get(
      `/subcategories/${subCategoryId}/products?${queryParams.toString()}`
    );
    return response?.data;
  } catch (error) {
    console.error("Error fetching products:", error);
    throw error;
  }
};

export const addToWishlist = async (productId) => {
  try {
    const response = await api.post("/wishlist", { productId: [productId] });
    return response;
  } catch (error) {
    console.error("Error adding to wishlist:", error);
    throw error;
  }
};

export const fetchWishlist = async () => {
  try {
    const response = await api.get("/wishlist");
    return response?.data;
  } catch (error) {
    console.error("Error fetching wishlist:", error);
    throw error;
  }
};

export const deleteFromWishlist = async (productId) => {
  try {
    const response = await api.delete(`/wishlist/${productId}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting from wishlist:", error);
    throw error;
  }
};

export const fetchProductDetails = async (productId) => {
  try {
    const response = await api.get(`/product/${productId}`);
    return response?.data;
  } catch (error) {
    console.error("Error fetching product details:", error);
    throw error;
  }
};

export const searchProducts = async (keyword) => {
  try {
    const response = await api.get(
      `/product/search?keyword=${encodeURIComponent(keyword)}`
    );

    if (response.data.message === "Products fetched successfully.") {
      return response.data.products;
    } else {
      throw new Error(
        response.data.message || "Failed to fetch search results"
      );
    }
  } catch (error) {
    console.error("Error searching products:", error);
    throw error;
  }
};
